import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchWatchlists,
  createWatchlist,
  deleteWatchlist,
  removeAktieFromWatchlist,
  selectAllWatchlists,
  selectWatchlistsStatus,
} from 'modules/aktien/features/watchlistsSlice';

function formatKurs(value, currency) {
  if (value == null) return '–';
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: currency || 'EUR',
  }).format(value);
}

function formatProz(value) {
  if (value == null) return '–';
  const v = Number(value);
  return `${v >= 0 ? '+' : ''}${v.toFixed(2)} %`;
}

/**
 * Ansicht „Meine Listen": Watchlists anlegen, ansehen und Aktien verwalten.
 */
export default function WatchlistsView() {
  const dispatch = useDispatch();
  const watchlists = useSelector(selectAllWatchlists);
  const status = useSelector(selectWatchlistsStatus);
  const [neuerName, setNeuerName] = useState('');

  useEffect(() => {
    dispatch(fetchWatchlists());
  }, [dispatch]);

  const handleCreate = (e) => {
    e.preventDefault();
    const name = neuerName.trim();
    if (!name) return;
    dispatch(createWatchlist(name));
    setNeuerName('');
  };

  const handleDeleteList = (uuid, name) => {
    if (window.confirm(`Liste „${name}" wirklich löschen?`)) {
      dispatch(deleteWatchlist(uuid));
    }
  };

  return (
    <div>
      <header className="mb-6">
        <h1 className="font-serif text-3xl font-semibold text-ink">Meine Listen</h1>
        <p className="mt-1 text-sm text-ink-muted">
          Lege eigene Listen an und sammle darin Aktien.
        </p>
      </header>

      {/* Neue Liste anlegen */}
      <form onSubmit={handleCreate} className="mb-8 flex gap-2">
        <input
          type="text"
          value={neuerName}
          onChange={(e) => setNeuerName(e.target.value)}
          placeholder="Name der neuen Liste…"
          className="w-full max-w-xs rounded-lg border border-line bg-paper-soft px-3 py-2 text-sm text-ink placeholder-ink-muted focus:border-ink/30 focus:outline-none focus:ring-2 focus:ring-ink/15"
        />
        <button
          type="submit"
          className="rounded-lg bg-ink px-4 py-2 text-sm font-medium text-paper transition-colors hover:bg-ink-soft"
        >
          Anlegen
        </button>
      </form>

      {status === 'loading' && watchlists.length === 0 && (
        <p className="text-ink-muted">Lade Listen…</p>
      )}

      {status !== 'loading' && watchlists.length === 0 ? (
        <p className="rounded-xl border border-dashed border-line bg-paper-soft p-8 text-center text-ink-muted">
          Noch keine Listen. Lege oben deine erste Liste an oder füge im Reiter
          „Indizes" Aktien hinzu.
        </p>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2">
          {watchlists.map((liste) => (
            <div
              key={liste.uuid}
              className="rounded-xl border border-line bg-paper-soft p-5"
            >
              <div className="mb-3 flex items-center justify-between">
                <h2 className="font-serif text-lg font-semibold text-ink">{liste.name}</h2>
                <button
                  type="button"
                  onClick={() => handleDeleteList(liste.uuid, liste.name)}
                  className="rounded-md px-2 py-1 text-xs text-ink-muted transition-colors hover:bg-loss/10 hover:text-loss"
                >
                  Liste löschen
                </button>
              </div>

              {(!liste.eintraege || liste.eintraege.length === 0) ? (
                <p className="text-sm text-ink-muted">Noch keine Aktien in dieser Liste.</p>
              ) : (
                <ul className="divide-y divide-line">
                  {liste.eintraege.map((e) => {
                    const positiv = (e.aenderungProz ?? 0) >= 0;
                    return (
                      <li key={e.isin} className="flex items-center justify-between py-2">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-ink">
                            {e.aktienName || e.isin}
                          </div>
                          <div className="font-mono text-xs text-ink-muted">
                            {e.indexName} · {e.isin}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 pl-3">
                          <div className="text-right">
                            <div className="font-mono tabular-nums text-sm text-ink">
                              {formatKurs(e.aktuellPreis)}
                            </div>
                            <div
                              className={`font-mono text-xs font-medium tabular-nums ${
                                positiv ? 'text-gain' : 'text-loss'
                              }`}
                            >
                              {formatProz(e.aenderungProz)}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              dispatch(
                                removeAktieFromWatchlist({ uuid: liste.uuid, isin: e.isin })
                              )
                            }
                            title="Aus Liste entfernen"
                            className="rounded-md px-2 py-1 text-ink-muted transition-colors hover:bg-loss/10 hover:text-loss"
                          >
                            ✕
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
